/**
 * Table Views Feature - NgRx Actions
 *
 * Defines all actions for table view state management using createActionGroup.
 * Actions are organized by operation type: load, select, CRUD, and default view management.
 *
 * @see Requirements 13.3, 13.6
 */
import { createActionGroup, emptyProps, props } from '@ngrx/store';

import {
  CreateTableViewPayload,
  SetDefaultViewPayload,
  ShareTableViewPayload,
  TableView,
  UpdateTableViewPayload,
} from '../model/table-view.model';

/**
 * Table Views actions grouped by feature.
 *
 * Naming convention: [Source] Event
 * - API actions: Load, Success, Failure triplets
 * - User actions: Select, Clear, Create, Update, Delete
 */
export const tableViewsActions = createActionGroup({
  source: 'Table Views',
  events: {
    // Load all views
    'Load Table Views': emptyProps(),
    'Load Table Views Success': props<{ views: TableView[] }>(),
    'Load Table Views Failure': props<{ error: string }>(),

    // Select/clear active view
    'Select Table View': props<{ uuid: string }>(),
    'Clear Active View': emptyProps(),

    // Create view
    'Create Table View': props<{ payload: CreateTableViewPayload }>(),
    'Create Table View Success': props<{ view: TableView }>(),
    'Create Table View Failure': props<{ error: string }>(),

    // Update view
    'Update Table View': props<{ payload: UpdateTableViewPayload }>(),
    'Update Table View Success': props<{ view: TableView }>(),
    'Update Table View Failure': props<{ error: string }>(),

    // Delete view
    'Delete Table View': props<{ uuid: string }>(),
    'Delete Table View Success': props<{ uuid: string }>(),
    'Delete Table View Failure': props<{ error: string }>(),

    // Set domain default view
    'Set Default View': props<{ payload: SetDefaultViewPayload }>(),
    'Set Default View Success': props<{ defaultViewUuid: string | null }>(),
    'Set Default View Failure': props<{ error: string }>(),

    // Toggle favourite
    'Toggle Favourite': props<{ uuid: string }>(),
    'Toggle Favourite Success': props<{ view: TableView }>(),
    'Toggle Favourite Failure': props<{ error: string }>(),

    // Share view
    'Share Table View': props<{ payload: ShareTableViewPayload }>(),
    'Share Table View Success': emptyProps(),
    'Share Table View Failure': props<{ error: string }>(),

    // Reset state
    'Reset State': emptyProps(),
  },
});
